import { Bell, PanelLeft } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { TopBarUser } from '@/components/common/TopBarUser';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import useAuth from '@/hooks/useAuth';
import { useAppSelector } from '@/store';
import { ROUTES, SALES_RM_ROLE } from '@/utils/constants';

const SEGMENT_LABELS: Record<string, string> = {
  approvals: 'Approvals',
  users: 'Users',
  sessions: 'Sessions',
  pincodes: 'Pincodes',
  'custom-cities': 'Custom Cities',
  'email-templates': 'Email Templates',
  'company-settings': 'Company Settings',
  'price-settings': 'Product Settings',
  slabs: 'Slabs',
  migration: 'Data Migration',
  dev: 'Dev Tools',
  profile: 'Profile',
};

const AdminBreadcrumbs = ({ path }: { path: string }) => {
  const parts = path
    .replace(/^\/admin/, '')
    .split('/')
    .filter(Boolean);
  // Record ids carry no list of their own, so they are dropped from the trail;
  // each remaining segment links to its cumulative path.
  const segments = parts.filter((p) => !/^\d+$/.test(p));
  if (segments.length === 0) segments.push('');

  let acc = '/admin';
  const crumbs = segments.map((segment) => {
    if (segment) acc += `/${segment}`;
    return {
      label:
        SEGMENT_LABELS[segment] ??
        (segment
          ? segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
          : 'Overview'),
      href: acc,
    };
  });

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs">
      <span className="font-semibold text-muted-foreground">Console</span>
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={`${crumb.label}-${crumb.href}`} className="flex items-center gap-1.5">
            <span className="text-muted-foreground">/</span>
            {isLast ? (
              <span className="font-bold text-foreground" aria-current="page">
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.href}
                className="rounded text-muted-foreground transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {crumb.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
};

interface AdminTopBarProps {
  onToggleSidebar: () => void;
}

export const AdminTopBar = ({ onToggleSidebar }: AdminTopBarProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSalesRm = useAppSelector((state) => state.auth.user?.jobRole === SALES_RM_ROLE);

  const roleLabel = user?.role === 'admin' ? 'Administrator' : (user?.jobRole ?? 'Staff');

  return (
    <header className="sticky top-0 z-40 h-14 border-b border-border bg-card/95 backdrop-blur">
      <div className="flex h-full items-center gap-3 px-4">
        <Button variant="ghost" size="icon" onClick={onToggleSidebar} aria-label="Toggle sidebar">
          <PanelLeft className="h-5 w-5" />
        </Button>

        <div className="flex-1">
          <AdminBreadcrumbs path={location.pathname} />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
              <Bell className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="border-b border-border px-3 py-2 text-sm font-bold">Notifications</div>
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              You&apos;re all caught up.
            </p>
          </PopoverContent>
        </Popover>

        {import.meta.env.DEV && (
          <Badge
            variant="outline"
            className="hidden border-warning bg-warning/30 text-foreground sm:inline-flex"
          >
            DEV
          </Badge>
        )}

        <TopBarUser
          profileRoute={ROUTES.ADMIN_PROFILE}
          roleLabel={roleLabel}
          extra={
            isSalesRm ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto shrink-0 py-1.5"
                onClick={() => {
                  void navigate(ROUTES.SALES_RM_LEADS);
                }}
              >
                Back to Sales RM
              </Button>
            ) : null
          }
        />
      </div>
    </header>
  );
};
